import { shallowRef } from "vue";
import { Environment } from "@/constants/env";
import type { useUserStore } from "@/store/user";
import type { MemberCycle } from "@/store/user/memberButton";

/** 智能客服 context 最近报错（对齐 holopix-support 运行时） */
export interface SupportWidgetLastError {
  code: string;
  message: string;
}

/** 签约状态（与 getRights.signStatus 对齐） */
export type SupportSignStatus = "UNSIGN" | "SIGN" | "CANCEL_SIGN";

/** 智能客服挂件 context 字段（与 @xmiles/support-widget 对齐） */
export interface SupportWidgetContextPayload {
  userId?: string;
  nickname?: string;
  /** 会员等级：free | vip | svip | ultra | team */
  plan?: string;
  /** 总算力（会员 + 永久 + 日算力） */
  credits?: number;
  page?: string;
  lastError?: SupportWidgetLastError;
  /** 用户来源 */
  source?: string;
  /** 注册时间 */
  registerTime?: string;
  /** 是否团队会员 */
  isTeam?: boolean;
  /** 当前计费周期类型（非「第几次续费」） */
  memberCycle?: string | null;
  /** 当前计费周期中文标签 */
  memberCycleLabel?: string | null;
  /** 会员算力余额 */
  memberCredits?: number;
  /** 永久算力余额 */
  foreverCredits?: number;
  /** 签约类型 */
  signStatus?: SupportSignStatus;
}

/** 计费周期中文标签（本地映射，避免拉入 pay 重依赖） */
const MEMBER_CYCLE_LABELS: Record<string, string> = {
  week: "7天",
  halfSubscribe: "连续包半月",
  subscribe: "连续包月",
  month: "月度",
  season: "季度",
  year: "年度",
  year_v1: "年度（再送2月）"
};

/** URL 开关：development/test/local 下注入 mock lastError，便于联调 */
export const SUPPORT_MOCK_ERROR_PARAM = "support_mock_error";

const DEV_ENVS = new Set<string>([Environment.test, Environment.local, Environment.development]);

const DEFAULT_MOCK_LAST_ERROR: SupportWidgetLastError = {
  code: "E4021",
  message: "渲染排队超时"
};

const lastErrorRef = shallowRef<SupportWidgetLastError | null>(null);

function readSearchParam(key: string): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(key);
}

/**
 * 团队态传 team；个人档原样传 free | vip | svip | ultra
 */
export function resolveSupportPlan(userStore: ReturnType<typeof useUserStore>): string {
  if (userStore.isGroupLogin) return "team";
  return userStore.highestPackage || "free";
}

/**
 * 将会员周期枚举转为中文标签；无周期时返回 null
 */
export function resolveMemberCycleLabel(cycle: MemberCycle | string | null | undefined): string | null {
  if (!cycle) return null;
  return MEMBER_CYCLE_LABELS[cycle] || String(cycle);
}

/**
 * development/test/local 且 URL 带 ?support_mock_error=1 时返回 mock 报错
 */
export function resolveMockLastError(
  env: string = import.meta.env.VITE_ENV,
  getParam: (key: string) => string | null = readSearchParam
): SupportWidgetLastError | undefined {
  if (!DEV_ENVS.has(env)) return undefined;
  if (getParam(SUPPORT_MOCK_ERROR_PARAM) !== "1") return undefined;
  return DEFAULT_MOCK_LAST_ERROR;
}

/** 上报最近报错（生图失败等） */
export function reportSupportLastError(error: { code?: string | number; message: string }) {
  lastErrorRef.value = {
    code: String(error.code ?? "UNKNOWN"),
    message: error.message
  };
}

/** 读取 lastError ref，供 useSupportWidget watch */
export function getSupportLastErrorRef() {
  return lastErrorRef;
}

/** 登出或卸载挂件时清空 */
export function clearSupportLastError() {
  lastErrorRef.value = null;
}

/** 解析最终写入 context 的 lastError：真实上报优先，其次 dev mock */
export function resolveSupportLastError(
  env: string = import.meta.env.VITE_ENV,
  getParam: (key: string) => string | null = readSearchParam
): SupportWidgetLastError | undefined {
  return lastErrorRef.value ?? resolveMockLastError(env, getParam) ?? undefined;
}

interface BuildSupportWidgetContextParams {
  userStore: ReturnType<typeof useUserStore>;
  page?: string;
}

/**
 * 组装 widget context（用户画像 + 当前页面 + lastError）
 * 字段来自 userInfo / getRights，供智能客服会话落库与运营后台展示
 */
export function buildSupportWidgetContext(params: BuildSupportWidgetContextParams): SupportWidgetContextPayload {
  const { userStore, page } = params;
  const lastError = resolveSupportLastError();
  const { userInfo, sources, currentMemberInfo } = userStore;
  const memberCycle = currentMemberInfo?.cycle ?? null;

  return {
    userId: userInfo.uniqueId || undefined,
    nickname: userInfo.nickname || undefined,
    plan: resolveSupportPlan(userStore),
    credits: userStore.powerNumber,
    page: page || undefined,
    source: userInfo.source || undefined,
    registerTime: userInfo.ctime ? String(userInfo.ctime) : undefined,
    isTeam: !!userStore.isGroupLogin,
    memberCycle,
    memberCycleLabel: resolveMemberCycleLabel(memberCycle),
    memberCredits: sources.dailyPower ?? 0,
    foreverCredits: sources.foreverPower ?? 0,
    signStatus: (sources.signStatus as SupportSignStatus) || "UNSIGN",
    ...(lastError ? { lastError } : {})
  };
}
